/**
 * 姿勢検出データを受け取ってパーティクルエフェクトを生成
 */
import * as THREE from 'three';
import { createScene } from './core/createScene';
import { createCamera } from './core/createCamera';
import { createRenderer } from './core/createRenderer';
import { Sizes } from './utils/Sizes';
import { createRaycaster } from './utils/createRaycaster';
import { Particles } from './world/Particle';
import { SETTINGS } from './SETTINGS';
import type { ProcessLandmarkerData, Landmark } from '../landmarker/HandLandmarkerApp';

export class WebGlParticle {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private sizes: Sizes;
  private particles: Particles;
  private particleSystem: THREE.Points;
  private particleMaterial: THREE.ShaderMaterial;
  private raycaster: THREE.Raycaster;

  // KeyPoint positions in 3D space
  private keyPoint3dArray: THREE.Vector3[] = []; // 検出したキーポイントの3D座標
  private isDetectedArray: boolean[] = []; // キーポイントが検出されたかどうかのフラグ

  constructor(canvas: HTMLCanvasElement) {
    // Initialize
    this.sizes = new Sizes();
    this.raycaster = createRaycaster();

    // Three.js setup
    this.scene = createScene();
    this.camera = createCamera(this.sizes.width / this.sizes.height);
    this.renderer = createRenderer(canvas);
    this.renderer.setSize(this.sizes.width, this.sizes.height);

    // Create particles
    this.particles = new Particles(this.renderer);
    this.particleSystem = this.particles.getInstance();
    this.particleMaterial = this.particles.getMaterial();
    this.scene.add(this.particleSystem);

    // Set up resize event listener
    this.setupEventListeners();
  }

  /**
   * 毎フレームの更新処理
   * @param poseData
   * @param elapsedTime
   */
  public update(poseData: ProcessLandmarkerData, elapsedTime: number): void {
    // Update shader time
    this.particleMaterial.uniforms.uTime.value = elapsedTime;
    // 検出されたキーポイントの3D座標を更新
    this.updateKeyPositions(poseData);
    // パーティクルを更新
    this.updateParticles(elapsedTime);
    // Render
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * 検出されたキーポイントの3D座標を取得
   * @returns {THREE.Vector3[]} 検出されたキーポイントの3D座標の配列
   */
  public getDetectedKeyPoints(): THREE.Vector3[] {
    // 検出されたキーポイントを格納するための配列を準備
    const detectedKeyPoints: THREE.Vector3[] = [];
    this.isDetectedArray.forEach((isDetected, index) => {
      if (isDetected) detectedKeyPoints.push(this.keyPoint3dArray[index]);
    });

    return detectedKeyPoints;
  }

  public dispose(): void {
    this.renderer.dispose();
    this.scene.clear();
  }

  // ----- private ------------------------------------------------------------
  // ブラウザのウィンドウサイズが変更されたときに実行
  private setupEventListeners(): void {
    this.sizes.setResizeCallback(() => {
      this.camera.aspect = this.sizes.width / this.sizes.height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.sizes.width, this.sizes.height);
      this.renderer.setPixelRatio(this.sizes.pixelRatio);
      this.particleMaterial.uniforms.uSize.value = 30.0 * this.renderer.getPixelRatio();
    });
  }

  /**
  * MediaPipeの3D座標をWebGL座標系に変換するヘルパー関数
  * @param keyPointPosition MediaPipeの3D座標
  * @returns 変換された3D座標、変換に失敗した場合はnull
  */
  private convertMediaPipeTo3D(keyPointPosition: Landmark): THREE.Vector3 | null {
    if (!keyPointPosition) return null;

    // 2D(NDC)はそのまま指先のスクリーン位置を使い、zは距離の補正値として利用
    const ndc = new THREE.Vector2(keyPointPosition.x * 2 - 1, -keyPointPosition.y * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    const direction = this.raycaster.ray.direction.normalize();

    const baseDistance = 5; // 基準距離

    // directionをdistanceでスカラー倍算（方向ベクトルを指定した距離分だけ拡大/縮小）
    return this.camera.position.clone().add(direction.multiplyScalar(baseDistance));
  }

  /**
   * 検出されたキーポイントの3D座標を更新
   * @param poseData 姿勢検出データ
   */
  private updateKeyPositions(poseData: ProcessLandmarkerData): void {
    const { leftIndexFinger, rightIndexFinger } = poseData;
    const keyPointPositionsArray: Landmark[] = [];
    if (leftIndexFinger) keyPointPositionsArray.push(leftIndexFinger);
    if (rightIndexFinger) keyPointPositionsArray.push(rightIndexFinger);

    const count = keyPointPositionsArray.length; // 検出されたキーポイントの数
    this.keyPoint3dArray.length = count;
    this.isDetectedArray.length = count;

    for (let i = 0; i < count; i++) {
      // インスタンス未生成なら作成
      if (!this.keyPoint3dArray[i]) this.keyPoint3dArray[i] = new THREE.Vector3();

      const keyPointPosition = keyPointPositionsArray[i];
      const convertedPosition = this.convertMediaPipeTo3D(keyPointPosition);

      if (convertedPosition) {
        this.keyPoint3dArray[i].copy(convertedPosition);
        this.isDetectedArray[i] = true;
      } else {
        this.isDetectedArray[i] = false;
      }
    }
  }

  /**
 * パーティクルのライフサイクルを管理
 * @param elapsedTime 経過時間
 */
  private updateParticles(elapsedTime: number): void {
    const positionsAttribute = this.particleSystem.geometry.getAttribute('position'); // 位置情報
    const startTimesAttribute = this.particleSystem.geometry.getAttribute('aStartTime'); // 開始時間
    const lifetimesAttribute = this.particleSystem.geometry.getAttribute('aLifetime'); // 寿命
    const velocitiesAttribute = this.particleSystem.geometry.getAttribute('aVelocity'); // 速度

    // 検出されたキーポイントを格納するための配列を準備
    const detectedKeyPoints: THREE.Vector3[] = [];
    this.isDetectedArray.forEach((isDetected, index) => {
      if (isDetected) detectedKeyPoints.push(this.keyPoint3dArray[index]);
    });

    // 検出されたキーポイントがある場合のみパーティクルを更新
    if (detectedKeyPoints.length > 0) {
      const particleCount = positionsAttribute.count; // 全パーティクルの総数
      const particlesPerFrame = Math.min(SETTINGS.PARTICLE.FRAME_PARTICLE_COUNT, particleCount); // 毎フレームでチェックするパーティクルの数
      // 時間の経過と共にチェックする開始位置をずらす
      const startIndex = Math.floor(elapsedTime * 60) % particleCount;

      let needsUpdate = false;

      // 決められた数だけパーティクルをチェック
      for (let i = 0; i < particlesPerFrame; i++) {
        const index = (startIndex + i) % particleCount;
        const lifetime = lifetimesAttribute.getX(index); // パーティクルの寿命
        const startTime = startTimesAttribute.getX(index); // パーティクルの開始時間
        const age = elapsedTime - startTime; // パーティクルの寿命が尽きたかどうか

        // パーティクルの寿命が尽きたらリセット
        if (age > lifetime) {
          // ランダムに検出されたキーポイントを選択（左か右か）
          const selectedKeyPoint = detectedKeyPoints[Math.floor(Math.random() * detectedKeyPoints.length)];

          // 新しい位置を設定
          positionsAttribute.setXYZ(
            index,
            selectedKeyPoint.x + (Math.random() - 0.5) * 0.2,
            selectedKeyPoint.y + (Math.random() - 0.5) * 0.2,
            selectedKeyPoint.z + (Math.random() - 0.5) * 0.2
          );

          // Set new velocity
          velocitiesAttribute.setXYZ(
            index,
            (Math.random() - 0.5) * SETTINGS.PARTICLE.VELOCITY.X,
            Math.random() * SETTINGS.PARTICLE.VELOCITY.Y + 1.0,
            (Math.random() - 0.5) * SETTINGS.PARTICLE.VELOCITY.Z
          );

          // Reset timing
          startTimesAttribute.setX(index, elapsedTime);
          lifetimesAttribute.setX(index, Math.random() * 2 + 2);

          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        positionsAttribute.needsUpdate = true;
        velocitiesAttribute.needsUpdate = true;
        startTimesAttribute.needsUpdate = true;
      }
    }
  }
}
